import React, { useCallback, useState } from 'react';
import { Stack, Text } from '@adc/parallax-component-library';
import { RichText } from 'Components/utility';
import { SHOW_MODAL } from 'Reducers/modal/types';
import { getDifferenceInMinutesFromNow } from 'Utilities/dateHelpers';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
import { logout } from 'Utilities/session';
import { store } from 'Utilities/store';
import useApiError from './useApiError';
const useAuth = (onDisplayLockoutMessage) => {
    const [isLocked, setIsLocked] = useState(false);
    const [lockoutMessage, setLockoutMessage] = useState('');
    const [lockoutSubmessage, setlockoutSubmessage] = useState('');
    const { showApiErrorModal } = useApiError();
    const redirectOnError = useCallback((redirectUrl, statusCode) => {
        if (redirectUrl) {
            const separator = redirectUrl.includes('?') ? '&' : '?';
            window.location.replace(`${redirectUrl}${separator}errorcode=${statusCode}`);
        }
    }, []);
    const showLockoutMessage = useCallback((attemptsResetAt) => {
        const { time, difference } = getDifferenceInMinutesFromNow(attemptsResetAt);
        if (difference < 0) {
            return;
        }
        setIsLocked(true);
        setLockoutMessage(i18n.t('Login.modals.signInErrors.accountLocked'));
        setlockoutSubmessage(i18n.t('Login.modals.signInErrors.accountLockedPeriod', { time }));
        const lockoutInterval = setInterval(() => {
            const { time, difference } = getDifferenceInMinutesFromNow(attemptsResetAt);
            if (difference < 0) {
                clearInterval(lockoutInterval);
                setLockoutMessage('');
                setlockoutSubmessage('');
                setIsLocked(false);
            }
            else {
                setlockoutSubmessage(i18n.t('Login.modals.signInErrors.accountLockedPeriod', { time }));
            }
        }, 1000);
        onDisplayLockoutMessage && onDisplayLockoutMessage();
    }, [onDisplayLockoutMessage]);
    const handleLockoutError = useCallback((error) => {
        const attemptsResetAt = Number(error.response?.headers['x-attempts-reset-at']);
        showLockoutMessage(attemptsResetAt);
    }, [showLockoutMessage]);
    const showLoginFailure = useCallback((attemptsRemaining = -1, attemptsResetAfter, attemptsResetAt, onCloseAfterLockout, onClose) => {
        const modalProps = {
            details: (React.createElement(Stack, { gap: 4 },
                React.createElement(Text, { color: "$text.100", type: "$bodyBase.default", nativeID: "Login.modals.signInErrors.loginFailure", testID: "Login.modals.signInErrors.loginFailure", marginBottom: "$1" }, i18n.t('Login.modals.signInErrors.loginFailure')),
                attemptsRemaining >= 0 ? (React.createElement(Stack, { display: "inline" },
                    React.createElement(Text, { color: "$text.100", type: "$bodyBase.default", nativeID: "Login.modals.signInErrors.attemptsRemaining", testID: "Login.modals.signInErrors.attemptsRemaining" }, i18n.t(`${attemptsRemaining === 1
                        ? 'Login.modals.signInErrors.attemptsRemaining'
                        : 'Login.modals.signInErrors.attemptsRemaining_other'}`, {
                        count: attemptsRemaining,
                    })),
                    attemptsResetAfter ? (React.createElement(Text, { color: "$text.100", type: "$bodyBase.medium", nativeID: "Login.modals.signInErrors.accountWillBeLocked", testID: "Login.modals.signInErrors.accountWillBeLocked" }, i18n.t('Login.modals.signInErrors.accountWillBeLocked', {
                        time: attemptsResetAfter,
                        duration: attemptsResetAfter,
                    }))) : null)) : null)),
            testID: 'Auth',
        };
        modalProps.onClose = () => {
            onClose && onClose();
        };
        if (attemptsResetAfter && attemptsResetAt) {
            modalProps.onClose = () => {
                showLockoutMessage(attemptsResetAt);
                onCloseAfterLockout && onCloseAfterLockout();
            };
        }
        store.dispatch({
            type: SHOW_MODAL,
            modalProps,
        });
    }, [showLockoutMessage]);
    const handleLoginError = useCallback((error, onCloseAfterLockout, onClose) => {
        if (error.response?.data.details === 'Authorization.isRequired') {
            logout();
            return;
        }
        if (error.response && error.response.headers['x-attempts-remaining']) {
            const attemptsRemaining = Number(error.response.headers['x-attempts-remaining']);
            const attemptsResetAt = error.response.headers['x-attempts-reset-at']
                ? Number(error.response.headers['x-attempts-reset-at'])
                : 0;
            const attemptsResetAfter = error.response.headers['x-attempts-reset-after']
                ? Number(error.response.headers['x-attempts-reset-after']) / 60
                : 0;
            showLoginFailure(attemptsRemaining, attemptsResetAfter, attemptsResetAt, onCloseAfterLockout, onClose);
            return;
        }
        showLoginFailure();
    }, [showLoginFailure]);
    const handleCountryRegionError = useCallback(() => {
        store.dispatch({
            type: SHOW_MODAL,
            modalProps: {
                details: i18n.t('Login.modals.signInErrors.regionDoesNotMatch'),
                testID: 'RegionError',
            },
        });
    }, []);
    const handleForgotPasswordDone = useCallback(() => {
        mediator.publish('router:navigate', '/login');
    }, []);
    const handleResetPasswordDone = useCallback(() => {
        mediator.publish('router:navigate', '/login');
    }, []);
    const showHcpError = useCallback(() => {
        store.dispatch({
            type: SHOW_MODAL,
            modalProps: {
                details: (React.createElement(Stack, { gap: 4 },
                    React.createElement(RichText, { text: i18n.t('Global.modals.blockHCPUser.body', {
                            appName: i18n.t('Global.appNames.libreView'),
                        }), testID: "Global.modals.blockHCPUser.body" }))),
                testID: 'Auth',
            },
        });
    }, []);
    const showTokenExpirationError = useCallback(() => {
        store.dispatch({
            type: SHOW_MODAL,
            modalProps: {
                details: (React.createElement(Stack, { gap: 4 },
                    React.createElement(RichText, { testID: "Global.modals.linkNotValid.title", text: i18n.t('Global.modals.linkNotValid.title') }),
                    React.createElement(Text, { color: "$text.100", type: "$bodyBase.default", nativeID: "Global.modals.linkNotValid.body", testID: "Global.modals.linkNotValid.body", marginTop: "$1" }, i18n.t('Global.modals.linkNotValid.body')))),
                testID: 'Auth',
            },
        });
    }, []);
    const showForgotPasswordBlockedMessage = useCallback(() => {
        store.dispatch({
            type: SHOW_MODAL,
            modalProps: {
                details: (React.createElement(Stack, { gap: 4 },
                    React.createElement(Text, { color: "$text.100", type: "$bodyBase.default", nativeID: "ForgotPassword.modals.forgotErrors.youReached", testID: "ForgotPassword.modals.forgotErrors.youReached", marginBottom: "$1" }, `${i18n.t('ForgotPassword.modals.forgotErrors.youReached')}`),
                    React.createElement(Text, { color: "$text.100", type: "$bodyBase.default", nativeID: "ForgotPassword.modals.forgotErrors.consider", testID: "ForgotPassword.modals.forgotErrors.consider", marginBottom: "$1" }, `${i18n.t('ForgotPassword.modals.forgotErrors.consider')}`))),
                testID: 'Auth',
            },
        });
    }, []);
    const showForgotPasswordConfirmatioMessage = useCallback(() => {
        store.dispatch({
            type: SHOW_MODAL,
            modalProps: {
                details: (React.createElement(Stack, { gap: 4 },
                    React.createElement(Text, { color: "$text.100", type: "$bodyBase.default", nativeID: "ForgotPassword.modals.forgotErrors.sentInstructions", testID: "ForgotPassword.modals.forgotErrors.sentInstructions", marginBottom: "$1" }, i18n.t('ForgotPassword.modals.forgotErrors.sentInstructions')),
                    React.createElement(Text, { color: "$text.100", type: "$bodyBase.default", nativeID: "ForgotPassword.modals.forgotErrors.notSeeingEmail", testID: "ForgotPassword.modals.forgotErrors.notSeeingEmail", marginBottom: "$1" }, i18n.t('ForgotPassword.modals.forgotErrors.notSeeingEmail')))),
                testID: 'Auth',
            },
        });
    }, []);
    const handleUnexpectedError = useCallback(() => {
        showApiErrorModal();
    }, [showApiErrorModal]);
    const showCountryRegionError = useCallback(() => {
        store.dispatch({
            type: SHOW_MODAL,
            modalProps: {
                details: i18n.t('Login.modals.signInErrors.regionDoesNotMatch'),
                testID: 'Auth',
            },
        });
    }, []);
    return {
        handleLoginError,
        handleLockoutError,
        handleCountryRegionError,
        handleForgotPasswordDone,
        handleResetPasswordDone,
        handleUnexpectedError,
        showHcpError,
        isLocked,
        lockoutMessage,
        lockoutSubmessage,
        showTokenExpirationError,
        showForgotPasswordBlockedMessage,
        showForgotPasswordConfirmatioMessage,
        showCountryRegionError,
        redirectOnError,
    };
};
export default useAuth;
